// A KAMPÁNY-form segédei: RHF-alak ↔ contract-body. A validálás forrás-igazsága a `@atlas/contracts`
// `CampaignCreateBodySchema` / `CampaignUpdateBodySchema` — itt NINCS saját Zod-séma (a repo szabálya).
//
// A form LAPOS és csak stringeket tart (a `accountIds` kivételével), mert az RHF-mezők üres állapota az üres
// string. Az üres opcionális mező `null`-ként megy a bodyba (mező-törlés), NEM üres stringként — a contract
// `nullableStr` preprocess-e ezt várja.
//
// A `clientId` a formban VAN, a bodyban NINCS: kizárólag a fiók-lista szűkítésére szolgál (kaszkád). Az
// ügyfelet a szerver deriválja a fiókból — ha a kliens küldhetné, egy elrontott pár a rossz ügyfélhez
// könyvelné a büdzsét.
import {
  CampaignCreateBodySchema,
  CampaignUpdateBodySchema,
  type CampaignDetail,
} from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';

export interface CampaignFormValues {
  /** CSAK a fiók-választó szűkítéséhez — nem megy a bodyba. */
  clientId: string;
  /** FELVÉTELNÉL: több fiók (fiókonként külön kampány jön létre). */
  accountIds: string[];
  /** SZERKESZTÉSNÉL: pontosan egy fiók. */
  accountId: string;
  name: string;
  ownerId: string;
  startsOn: string;
  endsOn: string;
  description: string;
  plannedBudget: string;
  actualBudget: string;
  currency: string;
  asanaTaskUrl: string;
}

export const EMPTY_CAMPAIGN_FORM: CampaignFormValues = {
  clientId: '',
  accountIds: [],
  accountId: '',
  name: '',
  ownerId: '',
  startsOn: '',
  endsOn: '',
  description: '',
  plannedBudget: '',
  actualBudget: '',
  currency: 'HUF',
  asanaTaskUrl: '',
};

/** Egy meglévő kampány → form-alak. */
export function toCampaignForm(row: CampaignDetail): CampaignFormValues {
  return {
    clientId: row.clientId,
    accountIds: [row.accountId],
    accountId: row.accountId,
    name: row.name,
    ownerId: row.ownerId,
    startsOn: row.startsOn,
    endsOn: row.endsOn,
    description: row.description ?? '',
    plannedBudget: row.plannedBudget ?? '',
    actualBudget: row.actualBudget ?? '',
    currency: row.currency,
    asanaTaskUrl: row.asanaTaskUrl ?? '',
  };
}

/** Üres/whitespace → `null` (mező-törlés); egyébként a trimmelt érték. */
const nullIfEmpty = (v: string): string | null => {
  const t = v.trim();
  return t === '' ? null : t;
};

function core(values: CampaignFormValues): Record<string, unknown> {
  return {
    name: values.name.trim(),
    ownerId: values.ownerId,
    startsOn: values.startsOn,
    endsOn: values.endsOn,
    description: nullIfEmpty(values.description),
    plannedBudget: nullIfEmpty(values.plannedBudget),
    actualBudget: nullIfEmpty(values.actualBudget),
    currency: values.currency,
    asanaTaskUrl: nullIfEmpty(values.asanaTaskUrl),
  };
}

export function toCampaignCreateBody(values: CampaignFormValues): unknown {
  return { ...core(values), accountIds: values.accountIds };
}

export function toCampaignUpdateBody(values: CampaignFormValues): unknown {
  return { ...core(values), accountId: values.accountId };
}

/**
 * A REZOLVEREK a contract sémáira épülnek, de a form ALAKJÁRA futnak — ezért a `values`-t előbb a body-alakra
 * fordítjuk, és a hibákat visszavetítjük a form-mezőkre. Az `accountIds`/`accountId` névazonos, a többi mező
 * 1:1, tehát a visszavetítés azonosság.
 */
function resolverFor(
  schema: typeof CampaignCreateBodySchema | typeof CampaignUpdateBodySchema,
  toBody: (values: CampaignFormValues) => unknown,
): Resolver<CampaignFormValues> {
  const base = zodResolver(schema) as unknown as Resolver<Record<string, unknown>>;
  return async (values, context, options) => {
    // A séma a BODY alakjára fut, nem a nyers formra: az üres opcionális mező a formban `''`, a bodyban
    // `null` — a formra futtatva egy üresen hagyott büdzsé-mező a decimal-regexen bukna.
    const result = await base(toBody(values) as Record<string, unknown>, context, options as never);
    const errors = result.errors;
    if (Object.keys(errors).length > 0) return { values: {}, errors };
    // A hibátlan ág a FORM alakját adja vissza (nem a bodyét): különben az RHF elvesztené a `clientId`-t,
    // ami csak a fiók-lista szűkítésére kell, és a bodyban nem is szerepel.
    return { values, errors: {} };
  };
}

export function buildCampaignCreateResolver(): Resolver<CampaignFormValues> {
  return resolverFor(CampaignCreateBodySchema, toCampaignCreateBody);
}

export function buildCampaignUpdateResolver(): Resolver<CampaignFormValues> {
  return resolverFor(CampaignUpdateBodySchema, toCampaignUpdateBody);
}
